import React from 'react';
import { ArrowRight, Diamond, Square, Globe, Award } from 'lucide-react';
import { aboutImage, stats } from '../mock';

const icons = [Award, Diamond, Square, Globe];

const About = () => {
  return (
    <section id="about" className="relative bg-[#0a0a0a] pt-28 pb-12 lg:pt-40 lg:pb-16 px-6 lg:px-12 overflow-hidden">
      {/* Centered header */}
      <div className="max-w-[1500px] mx-auto text-center mb-20 lg:mb-28">
        <h2 className="font-serif text-white text-6xl md:text-7xl lg:text-8xl leading-[1.05]">
          Sir Dion
        </h2>
        <h2
          className="font-serif italic text-[#c9a96e] text-6xl md:text-7xl lg:text-8xl leading-[1.05] -mt-2"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          Magee
        </h2>
        <div className="flex items-center justify-center gap-3 sm:gap-4 mt-8 max-w-full px-4">
          <span className="h-px w-6 sm:w-10 bg-[#c9a96e]/50 flex-shrink-0" />
          <p className="text-[#c9a96e]/85 text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.18em] sm:tracking-[0.28em] md:tracking-[0.35em] uppercase text-center">
            The Artist &nbsp;—&nbsp; Event &amp; Wedding Lifestyle Expert
          </p>
          <span className="h-px w-6 sm:w-10 bg-[#c9a96e]/50 flex-shrink-0" />
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* Left text */}
        <div className="order-2 lg:order-1">
          <div className="space-y-6 text-white/75 text-[15px] leading-[1.9] font-light max-w-xl">
            <p>
              Hi, I'm Sir Dion Magee. I am so excited to share our creative work with you. My team is passionate about creating, planning, and executing memorable events that transcend expectation.
            </p>
            <p>
              With over 25 years of service and 14 industry certifications, we bring a level of artistry and expertise to every event that simply cannot be replicated. You are in charge of the mission — we implement it flawlessly.
            </p>
          </div>

          {/* Stat cards */}
          <div className="mt-12 grid grid-cols-2 gap-4 max-w-xl">
            {stats.map((s, i) => {
              const Icon = icons[i];
              const hasNumber = !!s.number;
              return (
                <div
                  key={s.label || s.number}
                  className={`relative overflow-hidden border p-5 transition-colors duration-300 group ${
                    hasNumber
                      ? 'border-[#c9a96e]/45 bg-gradient-to-br from-[#1a1408]/60 to-transparent'
                      : 'border-white/10 hover:border-[#c9a96e]/40'
                  }`}
                >
                  {hasNumber ? (
                    <>
                      <div className="flex items-baseline gap-3">
                        <span
                          className="font-serif font-bold leading-none text-[#e8d5a8] text-[58px] md:text-[64px]"
                          style={{
                            textShadow:
                              '0 0 28px rgba(232,213,168,0.45), 0 0 60px rgba(201,169,110,0.3)',
                          }}
                        >
                          {s.number}
                        </span>
                        <span className="font-serif text-white text-base md:text-lg leading-tight">
                          {s.text}
                        </span>
                      </div>
                      <div className="text-white/45 text-[10px] tracking-[0.3em] uppercase mt-3">
                        {s.subtitle}
                      </div>
                    </>
                  ) : (
                    <>
                      <Icon className="text-[#c9a96e] mb-3" size={16} strokeWidth={1.5} />
                      <div className="text-white font-serif text-lg leading-tight">{s.label}</div>
                      <div className="text-white/40 text-[10px] tracking-[0.25em] uppercase mt-1">
                        {s.subtitle}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <a
            href="#contact"
            className="mt-12 inline-flex items-center gap-3 text-[#c9a96e] text-[11px] tracking-[0.35em] uppercase border-b border-[#c9a96e]/40 hover:border-[#c9a96e] pb-2 transition-colors"
          >
            Work With Us
            <ArrowRight size={14} />
          </a>
        </div>

        {/* Right image with corner frame */}
        <div className="order-1 lg:order-2 relative">
          <div className="relative aspect-[3/4] w-full max-w-[520px] mx-auto group">
            {/* Pulsing gold halo glow behind image */}
            <div
              className="absolute -inset-10 rounded-[40%] opacity-70 blur-3xl pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at center, rgba(232,213,168,0.45) 0%, rgba(201,169,110,0.25) 35%, transparent 70%)',
                animation: 'aboutGoldPulse 6s ease-in-out infinite',
              }}
            />
            <div
              className="absolute -inset-6 rounded-[30%] opacity-60 blur-2xl pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at 30% 70%, rgba(255,200,140,0.55) 0%, transparent 60%)',
                animation: 'aboutGoldPulse 8s ease-in-out infinite reverse',
              }}
            />

            {/* corner accents */}
            <span className="absolute -top-3 -right-3 w-14 h-14 border-t border-r border-[#c9a96e]/70 z-20 transition-colors duration-500 group-hover:border-[#e8d5a8]" />
            <span className="absolute -bottom-3 -left-3 w-14 h-14 border-b border-l border-[#c9a96e]/70 z-20 transition-colors duration-500 group-hover:border-[#e8d5a8]" />

            {/* Image frame with gold glow shadow */}
            <div
              className="relative w-full h-full overflow-hidden z-10"
              style={{
                boxShadow:
                  '0 0 60px rgba(232,213,168,0.25), 0 0 120px rgba(201,169,110,0.18), 0 20px 60px rgba(0,0,0,0.6)',
              }}
            >
              <img
                src={aboutImage}
                alt="Sir Dion Magee"
                className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
              />

              {/* Warm gold tint overlay */}
              <div
                className="absolute inset-0 pointer-events-none mix-blend-soft-light"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(232,213,168,0.35) 0%, transparent 35%, transparent 65%, rgba(201,169,110,0.3) 100%)',
                }}
              />

              {/* Inner gold edge glow */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  boxShadow:
                    'inset 0 0 80px 10px rgba(0,0,0,0.45), inset 0 0 30px rgba(232,213,168,0.18)',
                }}
              />

              {/* Animated diagonal shimmer sweep */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                  className="absolute top-0 -left-1/2 w-1/2 h-full"
                  style={{
                    background:
                      'linear-gradient(115deg, transparent 0%, transparent 40%, rgba(255,235,190,0.18) 50%, transparent 60%, transparent 100%)',
                    animation: 'aboutShimmer 7s ease-in-out infinite',
                  }}
                />
              </div>

              {/* Bottom gradient for label legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent pointer-events-none" />

              <div className="absolute bottom-5 left-5 text-[#e8d5a8] text-[10px] tracking-[0.35em] uppercase z-10">
                Est. 1999
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes aboutGoldPulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes aboutShimmer {
          0% { transform: translateX(0) skewX(-15deg); }
          100% { transform: translateX(400%) skewX(-15deg); }
        }
      `}</style>
    </section>
  );
};

export default About;
